import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaCheckCircle,
  FaFileSignature,
  FaInfoCircle,
  FaPaperPlane,
  FaUniversity,
} from 'react-icons/fa';
import Header from '../Header/Header.js';
import Footer from '../Footer/Footer.js';
import { getApiBaseUrl } from '../../utils/api.js';
import './StreletsRequestPage.css';

const SERVICE_TITLE = 'Заявка на получение услуги системы пожарного мониторинга';

const contractGroups = [
  {
    title: 'Варианты договора',
    options: [
      'Договор на покупку объектовой станции (исп.2/ исп.Т2) «Стрелец-мониторинг»',
      'Договор на покупку Тандем IP-И',
      'Договор на техническое присоединение и монтаж объектовой станции «Стрелец-мониторинг»',
      'Договор на обслуживание объектовой станции «Стрелец-мониторинг»',
      'Договор на диспетчерские услуги',
    ],
  },
];

const initialFormState = {
  contractType: '',
  inn: '',
  fullName: '',
  shortName: '',
  kpp: '',
  ogrn: '',
  legalAddress: '',
  postalAddress: '',
  phone: '',
  email: '',
  directorPosition: '',
  directorName: '',
  authorityBasis: '',
  bik: '',
  bankName: '',
  settlementAccount: '',
  correspondentAccount: '',
  consent: false,
  website: '',
};

const fieldConfig = [
  {
    name: 'inn',
    label: 'ИНН',
    inputMode: 'numeric',
    autoComplete: 'off',
    maxLength: 12,
    section: 'company',
  },
  {
    name: 'fullName',
    label: 'Полное наименование на русском языке',
    autoComplete: 'organization',
    section: 'company',
  },
  {
    name: 'shortName',
    label: 'Сокращенное наименование (если имеется)',
    autoComplete: 'organization',
    section: 'company',
  },
  {
    name: 'kpp',
    label: 'КПП',
    inputMode: 'numeric',
    autoComplete: 'off',
    maxLength: 9,
    section: 'company',
  },
  {
    name: 'ogrn',
    label: 'ОГРН',
    inputMode: 'numeric',
    autoComplete: 'off',
    maxLength: 15,
    section: 'company',
  },
  {
    name: 'legalAddress',
    label: 'Юридический адрес',
    autoComplete: 'street-address',
    section: 'company',
    isFull: true,
  },
  {
    name: 'postalAddress',
    label: 'Почтовый адрес',
    autoComplete: 'street-address',
    section: 'company',
    isFull: true,
  },
  {
    name: 'phone',
    label: 'Телефон организации',
    inputMode: 'tel',
    autoComplete: 'tel',
    section: 'company',
  },
  {
    name: 'email',
    label: 'Электронная почта для отправки Договора и Счета',
    inputMode: 'email',
    autoComplete: 'email',
    section: 'company',
  },
  {
    name: 'directorPosition',
    label: 'Должность руководителя',
    autoComplete: 'organization-title',
    section: 'company',
  },
  {
    name: 'directorName',
    label: 'ФИО руководителя',
    autoComplete: 'name',
    section: 'company',
  },
  {
    name: 'authorityBasis',
    label: 'Действует на основании',
    autoComplete: 'off',
    section: 'company',
  },
  {
    name: 'bik',
    label: 'БИК',
    inputMode: 'numeric',
    autoComplete: 'off',
    maxLength: 9,
    section: 'bank',
  },
  {
    name: 'bankName',
    label: 'Наименование банка',
    autoComplete: 'organization',
    section: 'bank',
  },
  {
    name: 'settlementAccount',
    label: 'Расчетный счет',
    inputMode: 'numeric',
    autoComplete: 'off',
    maxLength: 20,
    section: 'bank',
  },
  {
    name: 'correspondentAccount',
    label: 'Корреспондентский счет',
    inputMode: 'numeric',
    autoComplete: 'off',
    maxLength: 20,
    section: 'bank',
  },
];

function onlyDigits(value) {
  return value.replace(/\D/g, '');
}

function hasValidInnChecksum(value) {
  const inn = onlyDigits(value);

  if (inn.length === 10) {
    const coefficients = [2, 4, 10, 3, 5, 9, 4, 6, 8];
    const checksum = coefficients.reduce(
      (sum, coefficient, index) => sum + coefficient * Number(inn[index]),
      0
    ) % 11 % 10;

    return checksum === Number(inn[9]);
  }

  if (inn.length === 12) {
    const firstCoefficients = [7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
    const secondCoefficients = [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
    const firstChecksum = firstCoefficients.reduce(
      (sum, coefficient, index) => sum + coefficient * Number(inn[index]),
      0
    ) % 11 % 10;
    const secondChecksum = secondCoefficients.reduce(
      (sum, coefficient, index) => sum + coefficient * Number(inn[index]),
      0
    ) % 11 % 10;

    return firstChecksum === Number(inn[10])
      && secondChecksum === Number(inn[11]);
  }

  return false;
}

function hasValidOgrnChecksum(value) {
  const ogrn = onlyDigits(value);

  if (ogrn.length === 13) {
    return Number(ogrn.slice(0, 12)) % 11 % 10 === Number(ogrn[12]);
  }

  if (ogrn.length === 15) {
    return Number(ogrn.slice(0, 14)) % 13 % 10 === Number(ogrn[14]);
  }

  return false;
}

function validateValues(values) {
  const errors = {};
  const textFields = fieldConfig.filter((field) => ![
    'inn',
    'kpp',
    'ogrn',
    'phone',
    'email',
    'bik',
    'settlementAccount',
    'correspondentAccount',
  ].includes(field.name));

  if (!values.contractType) {
    errors.contractType = 'Выберите вариант договора.';
  }

  textFields.forEach((field) => {
    if (values[field.name].trim().length < 2) {
      errors[field.name] = 'Заполните поле.';
    }
  });

  if (!hasValidInnChecksum(values.inn)) {
    errors.inn = 'ИНН введен неправильно.';
  }

  if (!/^\d{9}$/.test(onlyDigits(values.kpp))) {
    errors.kpp = 'КПП должен содержать 9 цифр.';
  }

  if (!hasValidOgrnChecksum(values.ogrn)) {
    errors.ogrn = 'ОГРН введен неправильно.';
  }

  if (onlyDigits(values.phone).length < 10) {
    errors.phone = 'Укажите корректный телефон организации.';
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(values.email.trim())) {
    errors.email = 'Укажите корректную электронную почту.';
  }

  if (!/^\d{9}$/.test(onlyDigits(values.bik))) {
    errors.bik = 'БИК должен содержать 9 цифр.';
  }

  if (!/^\d{20}$/.test(onlyDigits(values.settlementAccount))) {
    errors.settlementAccount = 'Расчетный счет должен содержать 20 цифр.';
  }

  if (!/^\d{20}$/.test(onlyDigits(values.correspondentAccount))) {
    errors.correspondentAccount = 'Корреспондентский счет должен содержать 20 цифр.';
  }

  if (!values.consent) {
    errors.consent = 'Нужно подтвердить согласие на обработку персональных данных.';
  }

  return errors;
}

async function parseResponse(response) {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  return { message: await response.text() };
}

function buildMessage(values) {
  return [
    SERVICE_TITLE,
    '',
    `Вариант договора: ${values.contractType}`,
    '',
    'Реквизиты организации:',
    `ИНН: ${values.inn}`,
    `Полное наименование: ${values.fullName}`,
    `Сокращенное наименование: ${values.shortName}`,
    `КПП: ${values.kpp}`,
    `ОГРН: ${values.ogrn}`,
    `Юридический адрес: ${values.legalAddress}`,
    `Почтовый адрес: ${values.postalAddress}`,
    `Телефон организации: ${values.phone}`,
    `Электронная почта для отправки Договора и Счета: ${values.email}`,
    `Должность руководителя: ${values.directorPosition}`,
    `ФИО руководителя: ${values.directorName}`,
    `Действует на основании: ${values.authorityBasis}`,
    '',
    'Банковские реквизиты:',
    `БИК: ${values.bik}`,
    `Наименование банка: ${values.bankName}`,
    `Расчетный счет: ${values.settlementAccount}`,
    `Корреспондентский счет: ${values.correspondentAccount}`,
  ].join('\n');
}

function ContractOption({ option, selected, onChange }) {
  return (
    <label
      className={`streletsRequestPage__option${
        selected ? ' streletsRequestPage__option_selected' : ''
      }`}
    >
      <input
        type="radio"
        name="contractType"
        value={option}
        checked={selected}
        onChange={onChange}
        className="streletsRequestPage__option-input"
      />
      <span className="streletsRequestPage__option-check" />
      <span className="streletsRequestPage__option-text">{option}</span>
    </label>
  );
}

function Field({ field, value, error, touched, onChange, onBlur }) {
  const isInvalid = Boolean(error && touched);

  return (
    <label
      className={`streletsRequestPage__field${
        field.isFull ? ' streletsRequestPage__field_full' : ''
      }`}
    >
      <span className="streletsRequestPage__label">
        {field.label}
        <span className="streletsRequestPage__required" aria-hidden="true">*</span>
      </span>
      <input
        type={field.inputMode === 'email' ? 'email' : 'text'}
        name={field.name}
        className={`streletsRequestPage__input${
          isInvalid ? ' streletsRequestPage__input_invalid' : ''
        }`}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        inputMode={field.inputMode || 'text'}
        autoComplete={field.autoComplete || 'off'}
        maxLength={field.maxLength}
        aria-invalid={isInvalid}
      />
      {isInvalid ? (
        <span className="streletsRequestPage__error">{error}</span>
      ) : null}
    </label>
  );
}

function StreletsRequestPage() {
  const [formData, setFormData] = useState(initialFormState);
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const errors = useMemo(() => validateValues(formData), [formData]);
  const isValid = Object.keys(errors).length === 0;

  function handleChange(event) {
    const { name, value, type, checked } = event.target;
    const nextValue = type === 'checkbox' ? checked : value;

    setFormData((current) => ({
      ...current,
      [name]: nextValue,
    }));
  }

  function handleBlur(event) {
    const { name } = event.target;

    setTouched((current) => ({
      ...current,
      [name]: true,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (formData.website) {
      return;
    }

    const nextTouched = Object.keys(initialFormState).reduce(
      (acc, key) => ({ ...acc, [key]: true }),
      {}
    );

    setTouched(nextTouched);
    setStatus({ type: '', message: '' });

    if (!isValid) {
      setStatus({
        type: 'error',
        message: 'Проверьте заполнение формы и исправьте отмеченные поля.',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/contact-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.directorName.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim(),
          organization: formData.fullName.trim(),
          service: SERVICE_TITLE,
          message: buildMessage(formData),
          recipient: 'vdpo',
          consent: formData.consent,
          officialCommunicationAcknowledgement: true,
        }),
      });
      const data = await parseResponse(response);

      if (!response.ok) {
        throw new Error(
          data.message || 'Не удалось отправить заявку. Попробуйте позже.'
        );
      }

      setStatus({
        type: 'success',
        message: 'Заявка отправлена. Мы свяжемся с вами после обработки данных.',
      });
      setFormData(initialFormState);
      setTouched({});
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Не удалось отправить заявку. Попробуйте позже.',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Header />
      <main className="content">
        <section className="streletsRequestPage">
          <section className="streletsRequestPage__hero">
            <p className="streletsRequestPage__kicker">Стрелец-Мониторинг</p>
            <h2 className="streletsRequestPage__title">
              {SERVICE_TITLE}
            </h2>
            <p className="streletsRequestPage__lead">
              Выберите вариант договора и заполните реквизиты организации.
              В первую очередь укажите ИНН компании и БИК банковских реквизитов.
              Если данные не будут найдены, заполните поля вручную.
            </p>
          </section>

          <form
            className="streletsRequestPage__form"
            onSubmit={handleSubmit}
            noValidate
          >
            <section className="streletsRequestPage__section">
              <div className="streletsRequestPage__section-heading">
                <FaFileSignature className="streletsRequestPage__section-icon" />
                <div>
                  <p className="streletsRequestPage__kicker">Выберите вариант договора</p>
                  <h3 className="streletsRequestPage__section-title">
                    Тип договора
                    <span className="streletsRequestPage__required" aria-hidden="true">*</span>
                  </h3>
                </div>
              </div>

              <div className="streletsRequestPage__contract-groups">
                {contractGroups.map((group) => (
                  <article className="streletsRequestPage__contract-group" key={group.title}>
                    <h4 className="streletsRequestPage__contract-title">{group.title}</h4>
                    <div className="streletsRequestPage__options">
                      {group.options.map((option) => (
                        <ContractOption
                          key={option}
                          option={option}
                          selected={formData.contractType === option}
                          onChange={handleChange}
                        />
                      ))}
                    </div>
                  </article>
                ))}
              </div>
              {errors.contractType && touched.contractType ? (
                <p className="streletsRequestPage__error">{errors.contractType}</p>
              ) : null}
            </section>

            <section className="streletsRequestPage__section">
              <div className="streletsRequestPage__section-heading">
                <FaInfoCircle className="streletsRequestPage__section-icon" />
                <div>
                  <p className="streletsRequestPage__kicker">Заполните реквизиты договора</p>
                  <h3 className="streletsRequestPage__section-title">
                    Данные организации
                  </h3>
                </div>
              </div>
              <div className="streletsRequestPage__grid">
                {fieldConfig
                  .filter((field) => field.section === 'company')
                  .map((field) => (
                    <Field
                      key={field.name}
                      field={field}
                      value={formData[field.name]}
                      error={errors[field.name]}
                      touched={touched[field.name]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  ))}
              </div>
            </section>

            <section className="streletsRequestPage__section">
              <div className="streletsRequestPage__section-heading">
                <FaUniversity className="streletsRequestPage__section-icon" />
                <div>
                  <p className="streletsRequestPage__kicker">Банковские реквизиты</p>
                  <h3 className="streletsRequestPage__section-title">
                    Данные банка
                  </h3>
                </div>
              </div>
              <div className="streletsRequestPage__grid">
                {fieldConfig
                  .filter((field) => field.section === 'bank')
                  .map((field) => (
                    <Field
                      key={field.name}
                      field={field}
                      value={formData[field.name]}
                      error={errors[field.name]}
                      touched={touched[field.name]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  ))}
              </div>
            </section>

            <label className="streletsRequestPage__trap" aria-hidden="true">
              <span>Сайт</span>
              <input
                type="text"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                value={formData.website}
                onChange={handleChange}
              />
            </label>

            <section className="streletsRequestPage__consent">
              <label className="streletsRequestPage__consent-label">
                <input
                  type="checkbox"
                  name="consent"
                  className="streletsRequestPage__checkbox"
                  checked={formData.consent}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <span>
                  Даю согласие на{' '}
                  <Link
                    to="/personal-data-consent/"
                    className="streletsRequestPage__link"
                  >
                    обработку персональных данных
                  </Link>
                  <span className="streletsRequestPage__required" aria-hidden="true">*</span>
                  .
                </span>
              </label>
              {errors.consent && touched.consent ? (
                <p className="streletsRequestPage__error">{errors.consent}</p>
              ) : null}
            </section>

            <div className="streletsRequestPage__actions">
              <button
                type="submit"
                className="streletsRequestPage__submit"
                disabled={!isValid || isSubmitting}
              >
                <FaPaperPlane />
                {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
              </button>
              {status.message ? (
                <p
                  className={`streletsRequestPage__status streletsRequestPage__status_${status.type}`}
                >
                  {status.type === 'success' ? <FaCheckCircle /> : null}
                  <span>{status.message}</span>
                </p>
              ) : null}
            </div>
          </form>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default StreletsRequestPage;
